<template>
  <div>
    <v-card class="pl-5 pr-5 pb-5">
      <h1>Publication</h1>
      <v-divider></v-divider>
      <Notification :notify="notify" />

      <div class="row mt-4" v-if="publication">
        <div class="col-12 col-sm-3 text-center">
          <v-img
            :src="publication.ThumbnailUrl || defaultThumbnail"
            max-width="180"
            contain
            class="mx-auto"
          ></v-img>
        </div>
        <div class="col-12 col-sm-9">
          <h2>{{ publication.Title }}</h2>
          <div class="caption grey--text mb-3">ISBN: {{ publication.ISBN }}</div>

          <div class="detail-row">
            <span class="detail-label">Edition</span>
            <span>{{ publication.Edition }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Publisher</span>
            <span>{{ publication.Publisher }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Published</span>
            <span>{{ publication.PublicationDate }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Collection</span>
            <span>{{ collectionLabel(publication.Collection) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Subjects</span>
            <span>
              <v-chip
                v-for="(subject, idx) in splitSubjects(publication.Subjects)"
                :key="idx"
                x-small
                class="mr-1 mb-1"
              >{{ subjectLabel(subject) }}</v-chip>
            </span>
          </div>

          <div class="mt-4">
            <div class="detail-label">Summary</div>
            <p>{{ publication.Summary }}</p>
          </div>

          <v-btn
            id="borrowPublication"
            color="primary"
            class="mt-3"
            :disabled="borrowing"
            @click="onBorrow"
          >{{ borrowing ? "Opening Bibliotech..." : "Borrow" }}</v-btn>
        </div>
      </div>

      <div class="row" v-if="!loading && !publication && !loadFailed">
        <div class="col-12">
          <p class="grey--text text-center mt-5">No publication found.</p>
        </div>
      </div>
    </v-card>

    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import Notification from "@/components/Notification";
import * as api from "@/api";

export default {
  name: "LtiPublicationView",
  components: {
    Notification,
  },
  created() {
    this.token = this.$route.query.token || "";
    this.pubId = this.$route.query.publicationId || "";
    this.lcpKey = this.$route.query.lcpKey || "";

    if (!this.token) {
      this.notify.type = 4;
      this.notify.errors.push(
        "Missing LTI session token. Please relaunch this page from Moodle."
      );
      return;
    }
    if (!this.pubId) {
      this.notify.type = 4;
      this.notify.errors.push(
        "Missing publication id. Please relaunch this page from Moodle."
      );
      return;
    }
    this.decodeToken();
    this.loadPublication();
  },
  beforeDestroy() {
    if (this.deepLinkAttemptTimer) {
      clearTimeout(this.deepLinkAttemptTimer);
    }
  },
  methods: {
    decodeToken() {
      try {
        const parts = this.token.split(".");
        if (parts.length < 2) throw new Error("Not a valid JWT (expected 3 parts).");
        const payload = JSON.parse(atob(this.padBase64(parts[1])));
        this.payload = payload;
        this.organizationId =
          payload.organizationId || (payload.organization && payload.organization.id) || 0;
      } catch (e) {
        this.notify.type = 4;
        this.notify.errors.push("Failed to decode LTI session token: " + e.message);
      }
    },
    padBase64(str) {
      return str
        .replace(/-/g, "+")
        .replace(/_/g, "/")
        .padEnd(str.length + ((4 - (str.length % 4)) % 4), "=");
    },
    loadPublication() {
      this.loading = true;
      this.loadFailed = false;
      this.notify.type = 0;
      this.notify.errors = [];
      this.publication = null;
      api
        .postPaginByOrganizationLTI(
          "licenses/lti/publications/" + this.pubId,
          this.paging,
          this.organizationId,
          this.token
        )
        .then((response) => {
          const items = (response.data && response.data.data) || [];
          this.publication = items.length ? items[0] : null;
        })
        .catch((error) => {
          this.loadFailed = true;
          this.notify.type = 4;
          this.notify.errors.push(this.describeLoadError(error));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    describeLoadError(error) {
      const status = error && error.response && error.response.status;
      if (status === 401 || status === 403) {
        return "Your session has expired. Please relaunch this page from Moodle.";
      }
      return "Failed to load publication. Please try again.";
    },
    subjectLabel(subject) {
      if (subject == null) return "";
      return typeof subject === "string" ? subject : subject.SubjectName || subject.name || "";
    },
    splitSubjects(subjects) {
      if (Array.isArray(subjects)) return subjects;
      if (typeof subjects !== "string" || !subjects) return [];
      return subjects
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s);
    },
    collectionLabel(collection) {
      if (collection == null) return "-";
      return typeof collection === "string" ? collection : collection.Name || collection.name || "-";
    },
    detectPlatform() {
      const ua = navigator.userAgent || navigator.vendor || "";
      const platform = navigator.platform || "";
      if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
      if (/Android/i.test(ua)) return "android";
      if (/Win/i.test(platform) || /Windows/i.test(ua)) return "windows";
      return "desktopOther";
    },
    navigateTopOrSelf(url) {
      try {
        window.top.location.href = url;
      } catch (e) {
        window.location.href = url;
      }
    },
    storeUrlFor(platformType) {
      if (platformType === "windows") return this.STORE_URL_WINDOWS;
      if (platformType === "android") return this.STORE_URL_ANDROID;
      if (platformType === "ios") return this.STORE_URL_IOS;
      return this.STORE_URL_DESKTOP_OTHER;
    },
    onBorrow() {
      if (!this.publication || !this.publication.uuid || this.borrowing) return;
      const platformType = this.detectPlatform();
      this.attemptDeepLink(this.publication.uuid, this.storeUrlFor(platformType));
    },
    attemptDeepLink(uuid, storeUrl) {
      this.borrowing = true;
      let fallbackHandled = false;
      const cleanup = () => {
        document.removeEventListener("visibilitychange", onSignal);
        window.removeEventListener("blur", onSignal);
        window.removeEventListener("pagehide", onSignal);
      };
      const onSignal = () => {
        fallbackHandled = true;
        cleanup();
      };
      document.addEventListener("visibilitychange", onSignal);
      window.addEventListener("blur", onSignal);
      window.addEventListener("pagehide", onSignal);

      const deepLinkUrl =
        `bibliotech://publication/book/${uuid}` +
        `?token=${encodeURIComponent(this.token)}` +
        `&lcpKey=${encodeURIComponent(this.lcpKey)}`;
      this.navigateTopOrSelf(deepLinkUrl);

      this.deepLinkAttemptTimer = setTimeout(() => {
        cleanup();
        this.borrowing = false;
        this.deepLinkAttemptTimer = null;
        if (!fallbackHandled && document.visibilityState !== "hidden") {
          this.navigateTopOrSelf(storeUrl);
        }
      }, 2000);
    },
  },
  data: () => ({
    token: "",
    pubId: 0,
    organizationId: 0,
    lcpKey: "",
    publication: null,
    loading: false,
    loadFailed: false,
    borrowing: false,
    deepLinkAttemptTimer: null,
    defaultThumbnail: "https://storage.googleapis.com/prothumia/eBooks/Default_Thumbnail.png",
    notify: {
      type: 0,
      message: "",
      errors: [],
    },
    paging: {
      count: 10,
      page: 1,
      size: 10,
      keyword: "",
      orderColumn: "",
      isASC: true,
      subject1Id: 0,
      subject2Id: 0,
      titie: "",
      publisher: "",
      publishedYear: "",
      skip: null,
    },
    payload: {},
    STORE_URL_WINDOWS: "https://apps.microsoft.com",
    STORE_URL_ANDROID: "https://play.google.com",
    STORE_URL_IOS: "https://apps.apple.com",
    STORE_URL_DESKTOP_OTHER: "https://bibliotechsl.com/downloads",
  }),
};
</script>

<style scoped>
.detail-row {
  display: flex;
  padding: 4px 0;
  border-bottom: 1px solid #eee;
}
.detail-label {
  font-weight: bold;
  width: 140px;
  flex-shrink: 0;
}
</style>


// WEBPACK FOOTER //
// src/views/LtiPublicationView.vue